import { Bookmark, Target } from 'lucide-react';
import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import sidebarConfig from '../data/sidebarConfig';

const Sidebar: React.FC = () => {
  const location = useLocation();
  const [showBookmarkTooltip, setShowBookmarkTooltip] = useState(false);
  return (
    <div className="w-16 bg-slate-900 text-white flex flex-col items-center py-4 fixed h-full shadow-lg z-20">
      {/* Logo */}
      <NavLink
        to="/home"
        className="flex flex-col items-center justify-center mb-6 focus:outline-none"
        title="Go to Dashboard"
      >
        <Target className="h-8 w-8 text-orange-500" />
      </NavLink>
      
      {/* Favorites Icon */}
      <div className="relative group">
        <button
          className="flex items-center justify-center h-12 w-12 rounded-md transition-colors hover:bg-slate-700 mb-4 focus:outline-none"
          onMouseEnter={() => setShowBookmarkTooltip(true)}
          onMouseLeave={() => setShowBookmarkTooltip(false)}
        >
          <Bookmark className="h-6 w-6 text-gray-300 hover:text-orange-400" />
        </button>
        
        {/* Bookmark Tooltip */}
        {showBookmarkTooltip && (
          <div className="absolute left-full top-0 z-30 ml-2">
            {/* Invisible bridge to prevent hover gap */}
            <div className="absolute -left-2 top-0 w-2 h-full"></div>
            <div className="bg-slate-800 text-sm rounded-lg shadow-xl p-4 w-72 border border-slate-700">
              <div className="text-center">
                <h3 className="text-white font-medium mb-3">Add your first bookmark for quick access.</h3>
                
                {/* Mock bookmark input field */}
                <div className="bg-slate-700 rounded p-3 mb-4">
                  <div className="bg-white rounded flex items-center justify-between px-3 py-2">
                    <div className="w-24 h-2 bg-gray-300 rounded"></div>
                    <Bookmark className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
                
                <p className="text-gray-300 text-xs leading-relaxed">
                  Select the bookmark icon next to any item in the navigation to add it here.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Divider */}
      <div className="w-8 h-px bg-slate-700 mb-4"></div>
      
      {/* Primary nav icons */}
      <nav className="flex-1 flex flex-col space-y-4">
        {sidebarConfig.map(({ label, icon: Icon, items, basePath }) => {
          const active = location.pathname.startsWith(basePath);
          const isDashboard = label === 'Dashboard';
          const isInbox = label === 'Inbox';
          const shouldShowSubmenu = !isDashboard && !isInbox;
          
          return (
            <div key={label} className="group relative flex justify-center">
              <NavLink
                to={basePath}
                className={`flex items-center justify-center h-12 w-12 rounded-md transition-colors ${active ? 'bg-slate-700 text-orange-400' : 'hover:bg-slate-700'}`}
              >
                <Icon className="h-6 w-6" />
              </NavLink>
              
              {/* Submenu for items with subitems */}
              {shouldShowSubmenu && (
                <div className="absolute left-full top-0 hidden group-hover:block z-30">
                  {/* Invisible bridge to prevent hover gap */}
                  <div className="absolute -left-2 top-0 w-2 h-full"></div>
                  <div className="bg-slate-800 text-sm rounded shadow-xl py-2 w-56 border border-slate-700">
                    <div className="px-4 py-2 font-semibold text-orange-400 border-b border-slate-700">
                      {label}
                    </div>
                    {items.map(({ label: itemLabel, path }) => (
                      <NavLink
                        key={path}
                        to={path}
                        className={({ isActive }) =>
                          `block px-4 py-2 hover:bg-slate-700 transition-colors ${isActive ? 'bg-slate-700 text-orange-400' : 'text-gray-200'}`
                        }
                      >
                        {itemLabel}
                      </NavLink>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Simple label tooltip for Dashboard and Inbox */}
              {!shouldShowSubmenu && (
                <div className="absolute left-full top-0 hidden group-hover:block z-30">
                  {/* Invisible bridge to prevent hover gap */}
                  <div className="absolute -left-2 top-0 w-2 h-full"></div>
                  <div className="bg-slate-800 text-sm rounded shadow-xl py-2 px-4 border border-slate-700">
                    <div className="font-semibold text-orange-400">
                      {label}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
};

export default Sidebar;
